import { memo, useMemo } from 'react';

import { merge } from '@/components';
import { Autosave, Flex, Heading, Text } from '@/components/core';
import { ValidationFilledLogo } from '@/components/logos';
import { toValidationId, useAutoUpdateValidation, useGetValidation } from '@/hooks/api';
import { useValidationConfigStore } from '@/store';
import { DivProps, formatDate } from '@/utils';

export type ValidationPanelHeaderProps = DivProps & {
  validationId: string;
};

const IValidationPanelHeader = ({ validationId, className, ...rest }: ValidationPanelHeaderProps) => {
  const { data: validation } = useGetValidation(toValidationId(validationId));
  const notifications = useValidationConfigStore((state) => state.notifications);

  const updates = useMemo(() => {
    return {
      config: {
        notifications: Object.fromEntries(
          notifications.map((notification) => {
            const { id, ...rest } = notification;
            return [id, { ...rest }];
          })
        ),
      },
    };
  }, [notifications]);

  const { isPending, isSuccess, error } = useAutoUpdateValidation(1000, toValidationId(validationId), updates);

  return (
    <div className={merge('gap-sm flex items-center', className)} {...rest}>
      <ValidationFilledLogo className="rounded-lg" variant="primary" size="xl" />
      <Flex col>
        <Heading>Manual review</Heading>
        <Text variant="tertiary">Last updated: {formatDate(validation?.updatedTime ?? validation?.createdTime)}</Text>
      </Flex>
      <div className="grow" />
      <Autosave isPending={isPending} isSuccess={isSuccess} error={error} />
    </div>
  );
};

export const ValidationPanelHeader = memo(IValidationPanelHeader) as typeof IValidationPanelHeader;
